import React, { useState } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Table,
  Badge,
  Box,
  StatusIndicator,
} from '@amzn/awsui-components-console';

export type JobStatus = 'RUNNING' | 'FAILED' | 'COMPLETE' | 'PENDING' | 'CANCELLED';

export interface JobEntry {
  jobId: string;
  name: string;
  status: JobStatus;
  lastRun?: string;
  jobType?: string;
}

export interface DashboardButton {
  label: string;
  style: 'primary' | 'normal';
  action: string;
  onClick?: () => void;
}

export interface JobDashboardProps {
  jobs: JobEntry[];
  summary?: string;
  buttons?: DashboardButton[];
  onButtonClick?: (action: string) => void;
}

const STATUS_INDICATOR_MAP: Record<JobStatus, 'success' | 'error' | 'in-progress' | 'pending' | 'stopped'> = {
  RUNNING: 'in-progress',
  FAILED: 'error',
  COMPLETE: 'success',
  PENDING: 'pending',
  CANCELLED: 'stopped',
};

const STATUS_BADGE_MAP: Record<JobStatus, 'green' | 'red' | 'blue' | 'grey'> = {
  RUNNING: 'blue',
  FAILED: 'red',
  COMPLETE: 'green',
  PENDING: 'grey',
  CANCELLED: 'grey',
};

export const JobDashboard: React.FC<JobDashboardProps> = ({
  jobs,
  summary,
  buttons = [],
  onButtonClick,
}) => {
  const [clickedAction, setClickedAction] = useState<string | null>(null);

  const handleClick = (btn: DashboardButton) => {
    setClickedAction(btn.action);
    btn.onClick?.();
    onButtonClick?.(btn.action);
  };

  const columnDefinitions = [
    {
      id: 'name',
      header: 'Job Name',
      cell: (item: JobEntry) => item.name,
      sortingField: 'name',
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item: JobEntry) => (
        <StatusIndicator type={STATUS_INDICATOR_MAP[item.status] ?? 'pending'}>
          {item.status}
        </StatusIndicator>
      ),
      sortingField: 'status',
    },
    {
      id: 'jobType',
      header: 'Type',
      cell: (item: JobEntry) =>
        item.jobType ? <Badge>{item.jobType}</Badge> : '-',
    },
    {
      id: 'lastRun',
      header: 'Last Run',
      cell: (item: JobEntry) =>
        item.lastRun
          ? new Date(item.lastRun).toLocaleString()
          : '-',
      sortingField: 'lastRun',
    },
  ];

  return (
    <div className="job-dashboard">
      <Container
        header={
          <Header
            variant="h3"
            actions={
              buttons.length > 0 ? (
                <SpaceBetween direction="horizontal" size="xs">
                  {buttons.map((btn, i) => (
                    <Button
                      key={i}
                      variant={btn.style === 'primary' ? 'primary' : 'normal'}
                      onClick={() => handleClick(btn)}
                      loading={clickedAction === btn.action}
                    >
                      {btn.label}
                    </Button>
                  ))}
                </SpaceBetween>
              ) : undefined
            }
            description={summary}
          >
            Jobs
          </Header>
        }
      >
        <Table
          columnDefinitions={columnDefinitions}
          items={jobs}
          variant="embedded"
          sortingDisabled
          empty={
            <Box textAlign="center" color="inherit" padding="l">
              <Box variant="strong">No jobs found</Box>
              <Box variant="p" color="text-body-secondary" padding={{ top: 's' }}>
                No jobs have been created yet.
              </Box>
            </Box>
          }
        />
      </Container>
    </div>
  );
};

export default JobDashboard;
